import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import Loading from "components/atoms/Loading";
import { BrandContext } from "components/context/BrandContext";

// Constants
import { post, get } from "utils/request";
import { handleSetErrorAndSwitchFormState, uploadImageToS3, validationForImage } from "utils/HelperFunctions";
import { getMagnetNamesForBrand } from "apis/brand";
import { getCreateMagnetURL } from "apis/magnets";
import { ROUTES, TOAST_CONFIG } from "../../constants";
import NotFound from "../NotFound";
import CreateMagnet from "./CreateMagnet";

const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/gif", "image/png"];

export const validateImageType = (value: any) => {
  if (value) {
    const type = value.match(/[^:]\w+\/[\w-+\d.]+(?=;|,)/)[0];
    return SUPPORTED_FORMATS.includes(type);
  }
  return false;
};

type FormFieldsType = "name" | "image_url" | "max_supply" | "description" | "magnetized_because" | "members_csv";

const formFields = {
  magnetized_because: "1",
  name: "0",
  image_url: "0",
  max_supply: "0",
  description: "0",
  benefits: "1",
  members_csv: "0",
};

export const magnetFormSchema = [
  yup.object().shape({
    name: yup.string().trim().required("* Required field"),
    image: validationForImage,
    max_supply: yup.string().trim().required("* Required field"),
    description: yup.string().trim().required("* Required field"),
    type: yup.string().trim().required("Something Went Wrong"),
  }),
  yup.object().shape({
    magnetized_because: yup.string().trim().required("* Required field"),
    benefits: yup.array().of(
      yup.object().shape({
        image: validationForImage,
        title: yup.string().required("* Required field"),
        description: yup.string().required("* Required field"),
      }),
    ),
  }),
];

type CreateMagnetPageProps = {
  // eslint-disable-next-line react/require-default-props
  defaultValues?: any;
};

const defaultBenefits = Array(3).fill({
  image: undefined,
  title: undefined,
  description: undefined,
});

export default function CreateMagnetPage({ defaultValues }: CreateMagnetPageProps) {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const { brandId } = useContext(BrandContext);
  const navigate = useNavigate();

  const [existingMagnetNames, setExistingMagnetNames] = useState<string[]>([]);

  const formMethods = useForm<CreateUpdateMagnetFormType>({
    resolver: yupResolver(magnetFormSchema[currentStep]),
    defaultValues: defaultValues || { benefits: defaultBenefits },
    mode: "onChange",
  });

  useEffect(() => {
    formMethods.setValue("type", window.location.pathname.includes("controlled") ? 1 : 0);
  }, []);

  const { getValues, handleSubmit, setError, trigger } = formMethods;
  // This useEffect prevents the user from skipping any form steps by manually changing the URL>
  // useEffect(() => {
  //   if (!getValues("name") || !getValues("description")) {
  //     searchParams.set("step", "0");
  //     setSearchParams(searchParams);
  //     return;
  //   }
  //   if (
  //     // eslint-disable-next-line operator-linebreak
  //     currentStep > 0 &&
  //     (!getValues("magnet_benefit") || !getValues("magnet_price"))
  //   ) {
  //     searchParams.set("step", "1");
  //     setSearchParams(searchParams);
  //   }
  // }, [currentStep]);

  const getAndSetExistingMagnetNames = () => {
    if (brandId) {
      get(getMagnetNamesForBrand(brandId), {
        processData: ({ data }) => {
          setExistingMagnetNames(data.magnets_name);
        },
        processError: (errorMessage) => {
          toast.error(errorMessage, TOAST_CONFIG);
        },
      });
    } else {
      toast.error("No brandId was found in localStorage", TOAST_CONFIG);
    }
  };

  useEffect(() => {
    getAndSetExistingMagnetNames();
  }, []);

  // function for handling the multi step form
  const handleSetFormStep = (step: string) => {
    setCurrentStep(Number(step));
  };

  const handleMagnetCreateSuccess = () => {
    toast("Magnet Created Successfully!", {
      position: "top-right",
    });
    setLoading(false);
    handleSetFormStep("1");
    navigate(ROUTES.BRAND_DASHBOARD);
  };

  // TODO: Redirect to error fields.
  const handleMagnetCreateError = (errorMessage: string, error: any, errorData: any) => {
    toast.error(errorMessage, TOAST_CONFIG);
    // formFields should be an array with form fields in reverse order of occurence.
    const step = handleSetErrorAndSwitchFormState<FormFieldsType>(errorData, formFields, setError);
    if (step) handleSetFormStep(step);
    setLoading(false);
  };

  async function uploadFeatureImagesToS3AndReturnMappedImages(
    benefits: CreateUpdateMagnetFormBenefit[],
  ): Promise<MagnetBenefit[]> {
    const benefitsWithImageUrls: MagnetBenefit[] = [];
    const uploadFeatureImagesResponse = await Promise.allSettled(benefits.map((f) => uploadImageToS3(f.image[0])));
    // TODO: Error handing for rejected images.
    uploadFeatureImagesResponse.forEach((r, idx) => {
      // @ts-ignore
      const feature: CreateUpdateMagnetFormBenefit = { ...benefits[idx] };
      delete feature.image;
      if (r.status === "fulfilled" && r.value) {
        // @ts-ignore
        feature.image_url = r.value as string;
      } else {
        toast.error("Failed to upload to S3");
      }
      // @ts-ignore
      benefitsWithImageUrls.push(feature);
    });
    return benefitsWithImageUrls;
  }

  // function for posting data to the server
  const handleCreateMagnet = async () => {
    if (!brandId) return;

    setLoading(true);
    const formValues = getValues();
    // Upload Images to S3
    //  TODO: Handle Error Cases here.
    // TODO: Handle Edge Case here where profile image upload fails.
    const benefits = await uploadFeatureImagesToS3AndReturnMappedImages(formValues.benefits);

    const magnetImageURL = await uploadImageToS3(formValues.image[0]);

    const payload = {
      name: formValues.name,
      description: formValues.description,
      magnetized_because: formValues.magnetized_because,
      max_supply: formValues.max_supply,
      image_url: magnetImageURL,
      benefits: JSON.stringify(benefits),
      image_hash: formValues.image_hash || null,
      type: formValues.type || 0,
      members_csv: formValues.members_csv,
      membership_conditions: formValues.membership_conditions,
    };

    post(getCreateMagnetURL(brandId), payload, {
      processData: handleMagnetCreateSuccess,
      processError: handleMagnetCreateError,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  const handleFormErrors = (errors: any) => {
    let step: string = "";
    Object.keys(errors).some((errorKey: string) => {
      const errorKeyWithType = errorKey as FormFieldsType;
      if (Object.keys(formFields).includes(errorKeyWithType)) {
        step = formFields[errorKeyWithType];
      }
      if (Number(step) === currentStep) return true;
      return false;
    });
    if (step) {
      handleSetFormStep(step);
    }
  };

  const onSubmit = () => {
    const magnetName: string = getValues("name");

    // Validate magnet name entered by user with existing magnets.
    if (existingMagnetNames.includes(magnetName)) {
      setError("name", { message: "Magnet with same name already exists.", type: "custom" });
      handleSetFormStep("0");
      return;
    }

    if (currentStep === 1) {
      handleCreateMagnet();
      return;
    }

    handleSetFormStep((currentStep + 1).toString());
  };

  // TODO: Render not found page in case of invalid value in form step as derived from URL.
  if (currentStep > 1 || currentStep < 0) {
    return <NotFound />;
  }

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...formMethods}>
      {loading && <Loading />}
      <CreateMagnet
        loading={loading}
        currentStep={currentStep}
        handleSetFormStep={handleSetFormStep}
        handleFormSubmit={handleSubmit(onSubmit, handleFormErrors)}
        trigger={trigger}
      />
    </FormProvider>
  );
}
